import React, { useState } from 'react'
import {
  CCard,
  CCardBody,
  CButton,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CFormInput,
  CAlert,
  CContainer,
  CRow,
  CCol,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilLockLocked, cilTrash, cilPowerStandby, cilWarning } from '@coreui/icons'
import { useNavigate } from 'react-router-dom'
import { useNotification } from '../../contexts/NotificationContext'
import { getApiUrl } from '../../utils/apiClient'

const SuspendedAccount = () => {
  const navigate = useNavigate()
  const { addToast } = useNotification()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  // Phase 2.5 RGPD : password requis pour DELETE /api/members/me.
  // Le compte suspendu reste authentifié (cookie httpOnly intact),
  // mais on demande le password en re-confirmation anti-action accidentelle.
  const [deletePassword, setDeletePassword] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const handleReactivate = async () => {
    try {
      setErrorMessage('') // Réinitialiser le message d'erreur
      console.log('Début de la réactivation du compte')

      // Security: userId in localStorage is for UI state only - auth handled via httpOnly cookies
      const userId = localStorage.getItem('userId')
      if (!userId) {
        navigate('/login')
        return
      }

      // D'abord, récupérer le statut pour avoir l'userId
      const statusResponse = await fetch(`${getApiUrl()}/members/status`, {
        credentials: 'include', // Include httpOnly cookies for authentication
      })

      if (!statusResponse.ok) {
        const statusText = await statusResponse.text()
        console.error('Réponse status non OK:', statusResponse.status, statusText)
        throw new Error('Erreur lors de la récupération du statut')
      }

      const statusData = await statusResponse.json()

      if (!statusData.userId) {
        throw new Error('Identifiant utilisateur non trouvé')
      }

      // Maintenant, faire la réactivation
      const response = await fetch(`${getApiUrl()}/members/${statusData.userId}/reactivate`, {
        method: 'POST',
        credentials: 'include', // Include httpOnly cookies for authentication
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Erreur lors de la réactivation du compte')
      }

      // Succès
      addToast('Succès', 'Compte réactivé', 'Votre compte a été réactivé avec succès')

      // On attend un peu avant la redirection pour que l'utilisateur puisse voir le message
      setTimeout(() => {
        window.location.href = '/dashboard'
      }, 1500)
    } catch (error) {
      console.error('Erreur de réactivation:', error)
      setErrorMessage(error.message)
      addToast('Erreur', 'Erreur', error.message)
    }
  }

  const handleDelete = async () => {
    try {
      if (deleteConfirmText !== 'SUPPRIMER') {
        return
      }
      if (!deletePassword) {
        setErrorMessage('Veuillez confirmer votre mot de passe.')
        return
      }

      // RGPD article 17 — droit à l'effacement.
      // DELETE /api/members/me avec body { password } : marque le compte
      // en deletion_status='pending', envoie email de confirmation J+7,
      // cron J+30 anonymise auto si pas de clic. Cookie httpOnly présent
      // = auth (pas besoin de userId en URL).
      const response = await fetch(`${getApiUrl()}/members/me`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: deletePassword }),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || 'Erreur lors de la suppression du compte')
      }

      // Succès : message neutre, le backend a renvoyé 202 Accepted.
      addToast(
        'Succès',
        'Demande enregistrée',
        'Un email de confirmation vous a été envoyé. Cliquez sur le lien dans les 7 jours, sinon votre compte sera anonymisé automatiquement à J+30.',
      )

      localStorage.removeItem('token')
      setShowDeleteModal(false)
      navigate('/login')
    } catch (error) {
      console.error('Erreur de suppression:', error)
      setErrorMessage(error.message)
      addToast('Erreur', 'Erreur', error.message)
    }
  }

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <CContainer>
        <CRow className="justify-content-center">
          <CCol md={8} lg={6} xl={5}>
            <div className="text-center mb-4">
              <div className="position-relative d-inline-block">
                <div
                  className="bg-danger rounded-circle p-4 d-inline-flex align-items-center justify-content-center"
                  style={{
                    width: '100px',
                    height: '100px',
                    animation: 'pulseAlert 2s infinite',
                  }}
                >
                  <CIcon
                    icon={cilLockLocked}
                    size="xxl"
                    className="text-white"
                    style={{ width: '40px', height: '40px' }}
                  />
                </div>
              </div>
            </div>

            <CCard className="border-0 shadow-lg">
              <CCardBody className="p-5">
                <h2 className="text-center mb-4 fw-bold">Compte Suspendu</h2>

                {errorMessage && (
                  <CAlert color="danger" className="d-flex align-items-center mb-4">
                    <CIcon icon={cilWarning} className="flex-shrink-0 me-2" />
                    <div>{errorMessage}</div>
                  </CAlert>
                )}

                <div className="text-center mb-4">
                  <p className="lead text-muted">Votre compte est actuellement suspendu.</p>
                  <p className="mb-4">
                    Vous pouvez le réactiver à tout moment ou choisir de le supprimer
                    définitivement.
                  </p>
                </div>

                <div className="d-grid gap-3 mt-4">
                  <CButton
                    color="primary"
                    size="lg"
                    onClick={handleReactivate}
                    className="btn-pill shadow-sm"
                    style={{
                      transition: 'all 0.3s ease',
                      transform: 'translateY(0)',
                      ':hover': {
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <CIcon icon={cilPowerStandby} className="me-2" />
                    Réactiver mon compte
                  </CButton>

                  <CButton
                    color="danger"
                    variant="ghost"
                    size="lg"
                    onClick={() => setShowDeleteModal(true)}
                    className="btn-pill"
                  >
                    <CIcon icon={cilTrash} className="me-2" />
                    Supprimer définitivement mon compte
                  </CButton>
                </div>
              </CCardBody>
            </CCard>

            <div className="text-center mt-4">
              <small className="text-muted">Besoin d'aide ? Contactez le support</small>
            </div>
          </CCol>
        </CRow>
      </CContainer>

      <style>
        {`
          @keyframes pulseAlert {
            0% {
              box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.4);
            }
            70% {
              box-shadow: 0 0 0 20px rgba(220, 53, 69, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
            }
          }

          .btn-pill {
            border-radius: 50px;
            padding: 12px 30px;
          }

          .btn:hover {
            transform: translateY(-2px);
            transition: transform 0.3s ease;
          }
        `}
      </style>

      <CModal
        visible={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        alignment="center"
      >
        <CModalHeader className="bg-danger text-white border-0">
          <CModalTitle>
            <CIcon icon={cilWarning} className="me-2" />
            Supprimer mon compte
          </CModalTitle>
        </CModalHeader>
        <CModalBody className="p-4">
          <CAlert color="warning" className="d-flex align-items-center mb-4">
            <CIcon icon={cilWarning} className="flex-shrink-0 me-2" />
            <div>
              <h6 className="alert-heading mb-1">Attention</h6>
              <p className="mb-0">
                La suppression de votre compte est définitive. Toutes vos données seront effacées et
                cette action ne pourra pas être annulée.
              </p>
            </div>
          </CAlert>

          {/* Bloc RGPD : explique le flow J+7 / J+30 (article 17) */}
          <CAlert color="info" className="mb-4">
            <h6 className="alert-heading mb-2">Droit à l'effacement (RGPD article 17)</h6>
            <ul className="mb-0 ps-3 small">
              <li>
                Vos données personnelles seront effacées. Vos publications resteront visibles,
                signées « Utilisateur supprimé ».
              </li>
              <li>
                Un email de confirmation vous sera envoyé. Cliquez sur le lien dans les{' '}
                <strong>7 jours</strong> pour effacer immédiatement votre compte.
              </li>
              <li>
                Sans confirmation, votre compte sera anonymisé automatiquement à{' '}
                <strong>J+30</strong>.
              </li>
              <li>
                Votre historique de paiement reste conservé conformément à nos obligations légales
                (10 ans, Code de commerce art. L123-22).
              </li>
            </ul>
          </CAlert>

          <div className="mt-4">
            <label className="form-label">Pour confirmer, tapez "SUPPRIMER" ci-dessous :</label>
            <CFormInput
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="mb-3 form-control-lg"
            />
          </div>

          {/* Password RGPD : re-confirmation anti-action accidentelle */}
          <div className="mt-3">
            <label className="form-label">Confirmez votre mot de passe :</label>
            <CFormInput
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              autoComplete="current-password"
              placeholder="Mot de passe"
              className="mb-2"
            />
          </div>
        </CModalBody>
        <CModalFooter className="bg-light">
          <CButton color="secondary" variant="ghost" onClick={() => setShowDeleteModal(false)}>
            Annuler
          </CButton>
          <CButton
            color="danger"
            disabled={deleteConfirmText !== 'SUPPRIMER' || !deletePassword}
            onClick={handleDelete}
          >
            <CIcon icon={cilTrash} className="me-2" />
            Supprimer définitivement
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  )
}

export default SuspendedAccount
